import React, { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { AkoyaAccount, FormValues } from '../../ManagedAkoyaLinkedAccounts.meta';
import { useAkoyaChangeNameMutation } from '../../../../api/useAkoyaChangeNameMutation';
import { useAkoyaUnlinkMutation } from '../../../../api/useAkoyaUnlinkMutation';
import { Box } from '../../../../../../CommonComponents/OPL/Box/Box';
import { Alert } from '../../../../../../CommonComponents/OPL/Alert/Alert';
import { Cell, HtmlTable } from '../../../../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { Text } from '../../../../../../CommonComponents/OPL/Text/Text';
import { Input } from '../../../../../../CommonComponents/OPL/Input/Input';
import { Button } from '../../../../../../CommonComponents/OPL/Button/Button';
import { ModalHeader } from '../../../../../../CommonComponents/OPL/Modal/ModalHeader';
import { ModalCloseButton } from '../../../../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalFooter } from '../../../../../../CommonComponents/OPL/Modal/ModalFooter';
import { Modal } from '../../../../../../CommonComponents/OPL/Modal/Modal';

interface SelectedAccount {
  institutionName: string;
  name: string;
  akoyaTokenId: number;
  index: number;
}

interface AccountsListProps {
  providerId: string;
  accounts: AkoyaAccount[];
}

export const AkoyaAccounts = ({ accounts }: AccountsListProps) => {
  const [selectedAccountIndex, setSelectedAccountIndex] = useState<null | number>(null);
  const [selectedAccount, setSelectedAccount] = useState<SelectedAccount | null>(null);

  const {
    isLoading: isChangingName,
    mutateAsync: changeNameMutation,
    error: changeNameError,
  } = useAkoyaChangeNameMutation();

  const {
    isLoading: isUnlinkingAccount,
    mutateAsync: unlinkAccount,
    error: unLinkError,
  } = useAkoyaUnlinkMutation();

  const { control, register, getValues } = useForm<FormValues>({
    defaultValues: {
      accounts,
    },
  });

  const { fields, remove } = useFieldArray({
    control,
    name: 'accounts',
  });

  return (
    <>
      {changeNameError && <Alert variant="error" message={changeNameError?.message} />}

      <HtmlTable variant="embedded">
        <table>
          <colgroup>
            <col width="70%" />
            <col width="30%" />
          </colgroup>

          <thead>
            <tr>
              <th colSpan={2}>
                <Text variant="smallCaps" textAlign="left" px="8">
                  Name
                </Text>
              </th>
            </tr>
          </thead>

          <tbody>
            {fields.length === 0 ? (
              <tr>
                <td colSpan={2}>
                  <Text p="16">You have not added any accounts</Text>
                </td>
              </tr>
            ) : (
              fields.map((field, index) => (
                <tr key={field.id}>
                  <Cell isFlex>
                    <Input
                      {...register(`accounts.${index}.name`)}
                      disabled={selectedAccountIndex !== index}
                    />
                  </Cell>

                  <td>
                    <Box display="flex" justifyContent="space-between">
                      <Button
                        variant="link"
                        disabled={selectedAccountIndex === index && isChangingName}
                        isLoading={selectedAccountIndex === index && isChangingName}
                        onClick={async () => {
                          try {
                            const { name, akoyaTokenId } = getValues(`accounts.${index}`);

                            if (selectedAccountIndex === index) {
                              await changeNameMutation({
                                name,
                                akoyaTokenId,
                              });
                              setSelectedAccountIndex(null);
                            } else {
                              setSelectedAccountIndex(index);
                            }
                          } catch (err) {
                            console.error(err);
                          }
                        }}
                      >
                        {selectedAccountIndex === index ? 'Save' : 'Rename'}
                      </Button>

                      <Button
                        variant="link"
                        onClick={async () => {
                          try {
                            const { institutionName, akoyaTokenId, name } = getValues(
                              `accounts.${index}`,
                            );

                            setSelectedAccount({
                              institutionName,
                              name,
                              akoyaTokenId,
                              index,
                            });
                          } catch (err) {
                            console.error(err);
                          }
                        }}
                      >
                        Unlink
                      </Button>
                    </Box>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </HtmlTable>

      {selectedAccount && (
        <Modal isOpen size="small" onRequestClose={() => setSelectedAccount(null)}>
          <ModalHeader variant="warning">Unlink {selectedAccount.name}?</ModalHeader>

          <ModalCloseButton onClick={() => setSelectedAccount(null)} />

          {unLinkError && (
            <Box px="24">
              <Alert variant="error" message={unLinkError?.message} />
            </Box>
          )}

          <ModalFooter>
            <Button variant="secondary" onClick={() => setSelectedAccount(null)} mr="8">
              Cancel
            </Button>

            <Button
              disabled={isUnlinkingAccount}
              isLoading={isUnlinkingAccount}
              onClick={async () => {
                try {
                  const { index, ...account } = selectedAccount;

                  await unlinkAccount(account);

                  remove(index);

                  setSelectedAccount(null);
                } catch (err) {
                  console.error(err);
                }
              }}
              mr="8"
            >
              Unlink
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};
